import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { useNavigate } from "react-router-dom";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchDriversData } from "../../store/drivers/actions";
import moment from 'moment';
import Swal from 'sweetalert2';
import axios from 'axios';
import { toast } from 'react-toastify';

const DEFAULT_PAGE_SIZE = 10;

const Drivers = props => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const { data, loading, error } = useSelector(state => state.Drivers);

    useEffect(() => {
        dispatch(fetchDriversData({ page: pageIndex + 1, limit: pageSize }));
    }, [dispatch, pageIndex, pageSize]);

    const handleBlockUnblock = async (action, id) => {
      Swal.fire({
        title: `Are you sure you want to ${
          action === "disable" ? "block" : "unblock"
        } the selected user?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E53935",
        cancelButtonColor: "#3085d6",
        confirmButtonText: `Yes, ${action === "disable" ? "block" : "unblock"} it!`,
      }).then(async result => {
        if (result.isConfirmed) {
          try {
            const token = localStorage.getItem("authToken")
            const response = await axios.post(
              `${process.env.REACT_APP_WEB_APP_URL}/api/web/users/${action}/${id}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            )
            if (response.status == 200) {
              toast.success(
                `User successfully ${
                  action == "disable" ? "blocked" : "unblocked"
                }.`
              )
              dispatch(fetchDriversData({ page: pageIndex + 1, limit: pageSize }))
            } else {
              toast.error(
                `Failed to ${action == "disable" ? "block" : "unblock"} user`
              )
            }
          } catch (error) {
            if (error.status == 409) {
              return Swal.fire({
                title: `The selected user has ${error?.response?.data?.data?.active_bookings_count} active bookings, blocking the user will cancel all these booking. Confirm block the user?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#E53935",
                cancelButtonColor: "#3085d6",
                confirmButtonText: `Yes, block it!`,
              }).then(async result => {
                if (result.isConfirmed) {
                  try {
                    const formData = new FormData();
                    formData.append("force", 1);
                    const token = localStorage.getItem('authToken')
                    const response2 = await axios.post(
                      `${process.env.REACT_APP_WEB_APP_URL}/api/web/users/${action}/${id}`,
                      formData,
                      {
                        headers: {
                          Accept: 'application/json',
                          Authorization: `Bearer ${token}`,
                          "Content-Type": "multipart/form-data",
                        },
                      }
                    )
                    if (response2.status == 200) {
                      toast.success( response2.message )
                      dispatch(fetchDriversData({ page: pageIndex + 1, limit: pageSize }))
                    } else {
                      toast.error( `Failed to block user.` )
                    }
                  } catch (error) {
                    toast.error( error.message || `Failed to block user.` )
                  }
                }
              })
            }
            toast.error( error.message || `Failed to ${action == "disable" ? "block" : "unblock"} user` )
          }
        }
      })
    }


    const columns = useMemo(
        () => [
          {
            header: "Image",
            accessorKey: "profile_pic",
            enableColumnFilter: false,
            cell: ({ row }) => (
              <img
                src={row.original.profile_pic || "assets/images/no-image.png"}
                alt="Profile"
                style={{ width: 40, height: 40, borderRadius: "50%" }}
              />
            )
          },
          {
            header: 'Name',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Email',
            accessorKey: 'email',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Phone',
            accessorKey: 'phone',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Status',
            accessorKey: 'status',
            enableColumnFilter: false,
            enableSorting: true,
            cell: ({ row }) => {
              const status = row.original.status?.toLowerCase();
              const isOnline = status == 'online';

              return (
                <span className={`badge ${isOnline ? 'bg-success' : 'bg-danger'}`}>
                  {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Offline'}
                </span>
              );
            }
          },
          {
            header: 'Is Active?',
            accessorKey: 'is_active',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Created At',
            accessorKey: 'timestamp',
            enableColumnFilter: false,
            enableSorting: true,
          },
          {
            header: 'Actions',
            accessorKey: 'actions',
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }) => {
              const driverId = row.original?.driverId;
              const isActive = row.original?.is_active;
              return (
                <div className='d-flex gap-3'>
                  <button
                    className="btn btn-sm btn-success"
                    onClick={() => navigate(`/drivers/${driverId}`)}
                    >
                    <i className="mdi mdi-eye"></i> View
                  </button>
                  {isActive === "YES" ? 
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleBlockUnblock("disable", driverId)}
                    >
                    <i className="mdi mdi-block-helper"></i> Block
                  </button>
                  : 
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={() => handleBlockUnblock("enable", driverId)}
                    >
                    <i className="mdi mdi-check-circle-outline"></i> Unblock
                  </button>}
                </div>
              );
            },
          }
        ],
        [navigate]
      );

    const totalRecords = data?.total ?? data?.data?.length ?? 0;

    const handlePaginationChange = (newPageIndex, newPageSize) => {
        setPageIndex(newPageIndex);
        setPageSize(newPageSize);
    };

    const transformedData = useMemo(() => {
      return data?.data?.map(item => {
          return {
              driverId: item.user_id,
              profile_pic: item.profile_pic || "assets/images/no-image.png",
              name: item.user_name || "N/A",
              email: item.email || "N/A",
              phone: item.phone_code + " " + item.mobno || "N/A",
              timestamp: moment(item.timestamp).format("DD-MM-YYYY") || "N/A",
              status: item.status || "N/A",
              is_active: item.is_active == 1 ? 'YES' : 'NO' || "N/A",
          };
      }) ?? [];
    }, [data]);

    document.title = "Drivers | Moeen";
    return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Drivers" />
        {loading ? (
        <Skeleton count={10} height={40} />
        ) : (
        <TableContainer
          columns={columns}
          data={transformedData || []}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder={`Search in ${totalRecords} results.`}
          pagination="pagination"
          paginationWrapper='dataTables_paginate paging_simple_numbers'
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          serverSidePagination={true}
          totalRowCount={totalRecords}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPaginationChange={handlePaginationChange}
        />
      )}
      </div>
    </div>
  );
};

Drivers.propTypes = {
  t: PropTypes.any
};

export default Drivers;